import React, { useState } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { Box, Button, Stack, Typography } from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { MedicareBaseRateGrid } from '../grids';
import { AddEditMedicareBaseRateDialog } from '../dialogs';
import { useGetCenterQuery, useUpdateCenterMutation } from '../../../../services/ConfigurationService';
import { MedicareBaseRateType } from '../../../../services/ConfigurationService/types/centerType';

export const MedicareConfigTab: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const company = queryParams.get("company") || "";
  
  // Fetch center data
  const { data: center, refetch } = useGetCenterQuery({ id: id || "", company }, { skip: !id || !company });
  const [updateCenter] = useUpdateCenterMutation();
  
  const [rateDialogOpen, setRateDialogOpen] = useState(false);
  const [editingRate, setEditingRate] = useState<MedicareBaseRateType | null>(null);
  
  const medicareRates = center?.medicareRates || [];

  const handleAddRate = () => {
    setEditingRate(null);
    setRateDialogOpen(true);
  };

  const handleEditRate = (rate: MedicareBaseRateType) => {
    setEditingRate(rate);
    setRateDialogOpen(true);
  };

  const handleSaveRate = async (rate: MedicareBaseRateType) => {
    if (!center) return;

    let updatedRates: MedicareBaseRateType[];
    
    if (editingRate) {
      updatedRates = medicareRates.map(existing => 
        existing.id === editingRate.id ? rate : existing
      );
    } else {
      updatedRates = [...medicareRates, rate];
    }

    updatedRates.sort((a, b) => b.year - a.year);

    const updatedCenter = {
      ...center,
      medicareRates: updatedRates
    };
    
    await updateCenter(updatedCenter);
    refetch();
  };

  const handleDeleteRate = async (rateToDelete: MedicareBaseRateType) => {
    if (!center) return;

    const updatedRates = medicareRates.filter(rate => rate.id !== rateToDelete.id);

    const updatedCenter = {
      ...center,
      medicareRates: updatedRates
    };

    await updateCenter(updatedCenter);
    refetch();
  };

  const handleCloseRateDialog = () => {
    setRateDialogOpen(false);
    setEditingRate(null);
  };

  return (
    <Stack spacing={3}>
      {/* Medicare Base Rates Section */}
      <Typography variant="h6" sx={{ fontWeight: 600 }}>
        Medicare Base Rates
      </Typography>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleAddRate}
        >
          Add Medicare Base Rate
        </Button>
      </Box>
      
      <MedicareBaseRateGrid 
        medicareRates={medicareRates}
        onRowSelect={handleEditRate}
      />

      <AddEditMedicareBaseRateDialog
        open={rateDialogOpen}
        onClose={handleCloseRateDialog}
        onSave={handleSaveRate}
        onDelete={handleDeleteRate}
        editingRate={editingRate}
        existingRates={medicareRates}
      />
    </Stack>
  );
};